import React, { ElementType } from 'react';
import { Link, LinkProps } from 'react-router-dom';
import Tippy from '@tippyjs/react';
import { useTranslation } from 'react-i18next';
import 'tippy.js/dist/tippy.css';
import 'tippy.js/animations/scale.css';
import { colors, media } from '../../../../App/ChakraTheme';
import {
  Box,
  BoxProps,
  Button,
  HStack,
  Progress,
  Text,
  VStack,
  useColorMode,
} from '@chakra-ui/react';
import styled, { css } from '../../../../consts/styled';
import { Thumbnails } from '../../hooks/api/types';
import DotInfo from '../../../../components/DotInfo';
import useTutorialInfo, { InfoStep } from '../../../../hooks/useTutorialInfo';
import { useIsMobile } from '../../../../hooks/isMobile';
import { useIsTablet } from '../../../../hooks/isTablet';

interface ShareButtonProps extends BoxProps {
  share: {
    canBeShared: number;
    onShare: () => any;
    text: string;
    disabled: boolean;
  };
}

const ShareButton: React.FC<ShareButtonProps> = ({ share, ...rest }) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const isTablet = useIsTablet();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  
  return (
    <Box
      w="full"
      display="flex"
      flexDirection={isMobile ? 'column' : 'row'}
      alignItems={isMobile ? 'flex-end' : 'center'}
      justifyContent="space-between"
      gap={2}
      {...rest}
    >
      <Text as="p" textStyle="h5" textAlign={isMobile ? 'right' : 'left'}>
        {share.text}
      </Text>
      {share.canBeShared !== 1 && share.canBeShared !== -1 && (
        <Tippy
          arrow={true}
          delay={800}
          theme="light"
          animation="scale"
          content={t('classComponent.shareTippy')}
        >
          <Button
            colorScheme="blue"
            color={isDark ? "white" : undefined}
            className="small"
            style={{
              width: 'fit-content',
              minWidth: isMobile || isTablet ? '100px' : '126px',
              maxWidth: isMobile || isTablet ? '160px' : '200px',
              flexShrink: 1,
              justifyContent: 'center',
              whiteSpace: 'normal',
              wordBreak: 'break-word',
              border: isDark ? "2px solid rgba(255,255,255,0.85)" : "1px solid rgba(255,255,255,0.6)",
              fontSize: isMobile || isTablet ? '11px' : '12px',
              padding: isMobile || isTablet ? '4px 6px' : '6px 8px',
              lineHeight: '1.1',
            }}
            _focus={{ boxShadow: 'none' }}
            _focusVisible={{ outline: '3px solid var(--stemi-focus-color)', outlineOffset: '2px' }}
            disabled={share.disabled}
            onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
              e.stopPropagation();
              e.preventDefault();
              share.onShare();
            }}
          >
            {t('classComponent.shareAll')}
          </Button>
        </Tippy>
      )}
    </Box>
  );
};

const LinkToBeWrapped: React.FC<LinkProps & { isEnabled: boolean }> = ({
  isEnabled,
  ...rest
}) => {
  if (isEnabled) {
    return <Link {...rest} />;
  }

  return (
    <div
      style={{
        cursor: isEnabled ? 'pointer' : 'auto',
        pointerEvents: isEnabled ? 'visible' : 'none',
      }}
      {...(rest as any)}
    />
  );
};

const ClassComponentWrapper = styled(LinkToBeWrapped)`
  border-radius: 4px;
  transition: box-shadow 0.3s ease-out, transform 0.3s ease-out,
    background-color 0.3s ease-out;
  transition-delay: 0.1s;
  transform: translateY(0);
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.15);
  display: block;

  &:focus-visible {
    outline: 3px solid var(--stemi-focus-color);
    outline-offset: 3px;
    border-radius: 4px;
  }

  .card-analytics-link:focus-visible {
    outline: 3px solid var(--stemi-focus-color);
    outline-offset: 3px;
    border-radius: 4px;
  }

  ${(p) => {
    if (p.isEnabled) {
      return css`
        &:hover {
          box-shadow: 0 8px 16px 0 rgba(0, 0, 0, 0.15);
          background-color: ${colors.applyOpacity(colors.mischka, 0.2)};
          transform: translateY(-2px);
        }
      `;
    }

    return css`
      opacity: 0.4;
      cursor: not-allowed;
    `;
  }}
  .content {
    padding: 24px;
    ${media.phone`padding: 16px;`};
  }

  img {
    object-fit: cover;
    height: 100%;
    width: 100%;
  }

  img:before {
    content: ' ';
    display: block;
    position: absolute;
    height: 75px;
    width: 75px;
    background-image: url(https://stemi-editor.ams3.cdn.digitaloceanspaces.com/platform/gallery/75.png);
  }

  .card-class-image {
    flex-shrink: 0;
    width: 75px;
    height: 75px;
    margin-right: 16px;
    border-radius: 4px;
    overflow: hidden;
    ${media.phone`
  width: 100px;
  height: 100px;
  margin-right: 0;
`};
  }

  .new-items {
    position: absolute;
    background: red;
    border-radius: 100px;
    height: 24px;
    width: 24px;
    right: 4px;
    top: 4px;
    color: #fff;
    justify-content: center;
    align-items: center;
    text-align: center;
    display: flex;
    font-weight: bold;
    font-size: 12px;
  }
`;

interface ClassComponentProps {
  to: string;
  name?: string;
  description?: string;
  progress?: number | null;
  share?: ShareButtonProps['share'];
  thumbnails?: Thumbnails;
  ImageComponent?: ElementType;

  canBeShared?: number | null;

  id?: string;
  classId?: string;
  programId?: string;
  isTeacherOrStaff?: boolean;

  isPublished?: boolean;
  isStaff?: boolean;
  isSelfLearning?: boolean;

  sameHeight?: boolean;
  sameWidth?: boolean;
  enabled?: boolean;
  isUnit?: boolean;
  showInfo?: boolean;
  newItems?: number;
  target?: string;
  edit?: boolean;
  editLink?: string;
  isChallenge?: boolean;
  isPublic?: boolean;
}

const ClassComponent: React.FC<ClassComponentProps> = (props) => {
  const {
    to,
    target,
    name,
    description,
    thumbnails,
    ImageComponent,
    progress,
    id,
    classId,
    isTeacherOrStaff,
    canBeShared,
    isStaff,
    isPublished,
    programId,
    isSelfLearning,
    isChallenge,
    isPublic,
    sameHeight = true,
    sameWidth = true,
    share = undefined,
    isUnit,
    showInfo,
    newItems,
    edit,
    editLink,
  } = props;
  let { enabled = true } = props;

  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';
  const { currentStep, nextStep } = useTutorialInfo();
  const isInfoShown = showInfo && currentStep === InfoStep.OPEN_CLASS;

  const sizeStyle = { height: 'auto', width: '' };

  if (sameHeight) {
    sizeStyle.height = '100%';
  }
  if (sameWidth) {
    sizeStyle.width = isMobile ? '' : '100%';
  }

  if (isUnit) {
    if (
      !isTeacherOrStaff &&
      (props.canBeShared == null || canBeShared === -1 || canBeShared === 0)
    ) {
      enabled = !!isSelfLearning || !!isChallenge || !!isPublic;
    }

    if (!isStaff && !isPublished) {
      enabled = false;
    }
  }

  return (
    <ClassComponentWrapper
      to={to}
      target={target}
      onClick={() => {
        nextStep();
      }}
      style={sizeStyle}
      isEnabled={enabled}
      data-cy="dashboard-class"
    >
      {newItems ? (
        <div className="new-items">
          <span>{Math.min(newItems, 9)}</span>
        </div>
      ) : null}
      <HStack
        w="full"
        align="start"
        className={'mobile-vertical content h-center'}
        style={sizeStyle}
      >
        {ImageComponent ? (
          <div className="card-class-image">
            <ImageComponent />
          </div>
        ) : thumbnails ? (
          <div className={'card-class-image'}>
            <img
              src={thumbnails?.['75x75'] || 'https://picsum.photos/75/75'}
              alt={name ?? 'class image'}
            />
          </div>
        ) : null}
        <VStack
          spacing={0}
          gap={4}
          w="full"
          h="full"
          flexGrow={1}
          align="start"
          justify="space-between"
        >
          <div>
            {name ? (
              <Text
                as="h4"
                textStyle="h4"
                mb={2}
                fontSize={isMobile ? '14px' : '18px'}
              >
                {name}
              </Text>
            ) : null}
            {description ? (
              <Text
                as="div"
                textStyle="bodyXSmall"
                color="#595959"
                mb="auto"
                noOfLines={6}
                fontSize={isMobile ? '12px' : '16px'}
              >
                {description}
              </Text>
            ) : null}
          </div>
          <VStack justify="space-between" spacing={0} gap={2} w="full">
            {progress ? (
              <Progress
                value={progress}
                h="10px"
                mt={6}
                colorScheme="limegreen"
                background="gray.300"
                borderRadius="30px"
                aria-label={`Unit progress: ${Math.round(progress)}%`}
              />
            ) : null}

            {!edit && share && enabled && <ShareButton share={share} />}
            {!edit && !isChallenge && isTeacherOrStaff && enabled && (
              <HStack justify="end" w="full">
                <Tippy
                  arrow={true}
                  delay={800}
                  theme="light"
                  animation="scale"
                  content={t('classComponent.analyticsTippy')}
                >
                  <Link
                    data-cy-analytics
                    className="card-analytics-link"
                    to={`/program/${programId}/analytics/?analytics-unit-id=${id}&study-group-id=${classId}`}
                  >
                    <Button
                      w={isMobile ? '100px' : '126px'}
                      variant="outline"
                      colorScheme="blue"
                      border={isDark ? "2px solid rgba(255,255,255,0.85)" : undefined}
                      color={isDark ? "white" : undefined}
                      tabIndex={-1}
                    >
                      {t('classComponent.analytics')}
                    </Button>
                  </Link>
                </Tippy>
              </HStack>
            )}
            {isInfoShown ? (
              <DotInfo
                info="Open class"
                style={{ position: 'absolute', top: '8px', right: '8px' }}
              />
            ) : null}
          </VStack>
          {edit && (
            <Button
              flexShrink={0}
              w={isMobile ? '100px' : '126px'}
              variant="outline"
              colorScheme="blue"
              className="small"
              alignSelf="end"
              style={{
                width: isMobile ? '100px' : '126px',
                justifyContent: 'center',
              }}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                e.stopPropagation();
                e.preventDefault();
                window.open(
                  editLink || `/admin/syllabus/program/${id}/change/`,
                );
              }}
            >
              {t('classComponent.edit')}
            </Button>
          )}
        </VStack>
      </HStack>
    </ClassComponentWrapper>
  );
};

export default ClassComponent;
